import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface EventPlannerTemplateProps {
    profile: PublicProfile
}

export function EventPlannerTemplate({ profile }: EventPlannerTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Navigation */}
            <nav
                className="sticky top-0 z-50 border-b backdrop-blur-md"
                style={{ backgroundColor: `${colors.colors.background}E6`, borderColor: colors.colors.secondary }}
            >
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1
                                className="text-2xl font-bold tracking-wide"
                                style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                            >
                                {profile.business_name}
                            </h1>
                            {profile.tagline && (
                                <p className="text-xs uppercase tracking-widest" style={{ color: colors.colors.textMuted }}>
                                    {profile.tagline}
                                </p>
                            )}
                        </div>
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#about" className="transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.event.about')}
                            </a>
                            <a href="#services" className="transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.event.services')}
                            </a>
                            <a href="#portfolio" className="transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.event.portfolio')}
                            </a>
                            <a href="#contact" className="transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.event.contact')}
                            </a>
                            <button
                                onClick={() => setIsBookingOpen(true)}
                                className="px-6 py-3 rounded-full font-medium transition-colors cursor-pointer"
                                style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                            >
                                {t('template.event.getQuote')}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section
                className="relative min-h-screen py-20 overflow-hidden"
                style={{ backgroundColor: colors.colors.secondary }}
            >
                <div className="absolute inset-0 opacity-20">
                    <div
                        className="absolute top-20 right-20 w-96 h-96 rounded-full blur-3xl"
                        style={{ backgroundColor: colors.colors.accent }}
                    />
                    <div
                        className="absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl"
                        style={{ backgroundColor: colors.colors.primary }}
                    />
                </div>
                <div className="max-w-7xl mx-auto px-4 relative z-10 min-h-screen flex items-center">
                    <div className="max-w-3xl">
                        <p
                            className="uppercase tracking-widest mb-4 text-sm font-semibold"
                            style={{ color: colors.colors.accent }}
                        >
                            {t('template.event.luxuryEventPlanning')}
                        </p>
                        <h1
                            className="text-5xl md:text-7xl font-bold leading-tight mb-6"
                            style={{ fontFamily: typography.heading, color: colors.colors.text }}
                        >
                            {t('template.event.creatingUnforgettableMoments')}
                        </h1>
                        <p className="text-xl mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                            {t('template.event.fromIntimateToGrand')}
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a
                                href="#contact"
                                className="inline-flex items-center px-8 py-4 rounded-full font-medium transition-all shadow-lg"
                                style={{
                                    backgroundColor: colors.colors.primary,
                                    color: colors.colors.secondary,
                                }}
                            >
                                {t('template.event.planYourEvent')}
                            </a>
                            <a
                                href="#portfolio"
                                className="inline-flex items-center px-8 py-4 border-2 rounded-full font-medium transition-all"
                                style={{
                                    borderColor: colors.colors.primary,
                                    color: colors.colors.primary,
                                }}
                            >
                                {t('template.event.viewPortfolio')}
                            </a>
                        </div>
                        <div className="flex items-center space-x-8 mt-12">
                            <div>
                                <p className="text-4xl font-bold" style={{ fontFamily: typography.heading, color: colors.colors.accent }}>
                                    500+
                                </p>
                                <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.event.eventsPlanned')}</p>
                            </div>
                            <div>
                                <p className="text-4xl font-bold" style={{ fontFamily: typography.heading, color: colors.colors.accent }}>
                                    18+
                                </p>
                                <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.event.yearsExperience')}</p>
                            </div>
                            <div>
                                <p className="text-4xl font-bold" style={{ fontFamily: typography.heading, color: colors.colors.accent }}>
                                    100%
                                </p>
                                <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.event.clientSatisfaction')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About */}
            {profile.bio && (
                <section id="about" className="py-24 px-4 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div className="relative">
                                {profile.profile_image ? (
                                    <img
                                        src={profile.profile_image}
                                        alt={profile.business_name}
                                        className="rounded-3xl shadow-2xl w-full object-cover"
                                    />
                                ) : (
                                    <div
                                        className="rounded-3xl shadow-2xl w-full aspect-[6/7] flex items-center justify-center"
                                        style={{ backgroundColor: colors.colors.secondary }}
                                    >
                                        <span
                                            className="text-6xl font-bold"
                                            style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                                        >
                                            {profile.business_name[0]}
                                        </span>
                                    </div>
                                )}
                                <div
                                    className="absolute -bottom-6 -right-6 rounded-2xl p-6 shadow-xl"
                                    style={{ backgroundColor: colors.colors.accent, color: colors.colors.secondary }}
                                >
                                    <p className="font-bold" style={{ fontFamily: typography.heading }}>{t('template.event.awardWinning')}</p>
                                    <p className="text-sm opacity-80">{t('template.event.eventPlanner')}</p>
                                </div>
                            </div>
                            <div>
                            <p className="uppercase tracking-widest mb-4 text-sm font-semibold" style={{ color: colors.colors.accent }}>
                                {t('template.event.aboutUs')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.event.whereDreamsBecomeReality')}
                            </h2>
                            <p className="text-lg mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {profile.bio}
                            </p>
                            <div className="grid grid-cols-2 gap-6 mb-8">
                                <div className="rounded-xl p-5" style={{ backgroundColor: colors.colors.secondary }}>
                                    <i className="fas fa-gem text-2xl mb-3" style={{ color: colors.colors.accent }} />
                                    <h4 className="font-bold">{t('template.event.fullService')}</h4>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.event.endToEndPlanning')}</p>
                                </div>
                                <div className="rounded-xl p-5" style={{ backgroundColor: colors.colors.secondary }}>
                                    <i className="fas fa-heart text-2xl mb-3" style={{ color: colors.colors.accent }} />
                                    <h4 className="font-bold">{t('template.event.weddingSpecialists')}</h4>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.event.yourPerfectDay')}</p>
                                </div>
                            </div>
                                {profile.social_links && profile.social_links.length > 0 && (
                                    <div className="flex items-center space-x-4">
                                        {profile.social_links.slice(0, 3).map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                                                style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                                            >
                                                <i className={`fab fa-${link.platform}`} />
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Services */}
            {profile.services && profile.services.length > 0 && (
                <section
                    id="services"
                    className="py-24 px-4"
                    style={{ backgroundColor: colors.colors.background }}
                >
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p className="uppercase tracking-widest mb-4 text-sm font-semibold" style={{ color: colors.colors.accent }}>
                                {t('template.event.ourServices')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.event.eventPackages')}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8">
                            {profile.services.map((service, index) => (
                                <div
                                    key={service.id}
                                    className={`rounded-3xl p-8 shadow-lg ${index === 1 ? 'scale-105 relative overflow-hidden' : ''
                                        }`}
                                    style={
                                        index === 1
                                            ? {
                                                backgroundColor: colors.colors.primary,
                                                color: colors.colors.secondary,
                                            }
                                            : {
                                                backgroundColor: colors.colors.background,
                                            }
                                    }
                                >
                                    {index === 1 && (
                                        <div
                                            className="absolute top-0 right-0 px-4 py-1 text-sm font-bold"
                                            style={{ backgroundColor: colors.colors.accent, color: colors.colors.primary }}
                                        >
                                            {t('template.event.mostPopular')}
                                        </div>
                                    )}
                                    <div
                                        className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                                        style={{ backgroundColor: index === 1 ? 'rgba(255,255,255,0.1)' : colors.colors.secondary }}
                                    >
                                        <i
                                            className={`fas fa-${index === 0 ? 'ring' : index === 1 ? 'star' : 'clipboard-check'
                                                } text-2xl`}
                                            style={{ color: colors.colors.accent }}
                                        />
                                    </div>
                                    <h3
                                        className="text-2xl font-bold mb-2"
                                        style={{ fontFamily: typography.heading }}
                                    >
                                        {service.name}
                                    </h3>
                                    {service.price_display !== 'hidden' && service.price && (
                                        <p className="text-3xl font-bold mb-4" style={{ color: colors.colors.accent }}>
                                            ${service.price.toFixed(0)}
                                            {service.price_display === 'from' && '+'}
                                        </p>
                                    )}
                                    {service.description && (
                                        <p
                                            className="mb-6"
                                            style={{ color: index === 1 ? 'rgba(255,255,255,0.7)' : colors.colors.textMuted }}
                                        >
                                            {service.description}
                                        </p>
                                    )}
                                    <a
                                        href="#contact"
                                        className="block w-full py-4 rounded-full font-medium text-center transition-colors"
                                        style={
                                            index === 1
                                                ? {
                                                    backgroundColor: colors.colors.accent,
                                                    color: colors.colors.primary,
                                                }
                                                : {
                                                    backgroundColor: colors.colors.primary,
                                                    color: colors.colors.secondary,
                                                }
                                        }
                                    >
                                        Get Quote
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Portfolio / Gallery */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section id="portfolio" className="py-24 px-4 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p className="uppercase tracking-widest mb-4 text-sm font-semibold" style={{ color: colors.colors.accent }}>
                                Portfolio
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.event.recentEvents')}
                            </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={6}
                            enableLightbox={true}
                            showCaptions={true}
                            gapSize={4}
                            aspectRatio="square"
                        />
                    </div>
                </section>
            )}

            {/* Hours / Availability */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="py-24 px-4" style={{ backgroundColor: colors.colors.secondary }}>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p className="uppercase tracking-widest mb-4 text-sm font-semibold" style={{ color: colors.colors.accent }}>
                                {t('template.event.consultations')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.event.availableHours')}
                            </h2>
                        </div>
                        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    {profile.availability.slice(0, 4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: colors.colors.secondary }}
                                        >
                                            <span className="font-semibold" style={{ color: colors.colors.text }}>
                                                {day.day_name}
                                            </span>
                                            <span style={{ color: colors.colors.textMuted }}>
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.closed')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-4">
                                    {profile.availability.slice(4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: colors.colors.secondary }}
                                        >
                                            <span className="font-semibold" style={{ color: colors.colors.text }}>
                                                {day.day_name}
                                            </span>
                                            <span style={{ color: colors.colors.accent, fontWeight: 'bold' }}>
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.byAppointment')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-8 text-center rounded-xl p-6" style={{ backgroundColor: `${colors.colors.accent}20` }}>
                                <p style={{ color: colors.colors.text }}>
                                    <i className="fas fa-calendar-check mr-2" style={{ color: colors.colors.accent }} />
                                    <strong>{t('template.event.eventDayServices247')}</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact */}
            <section id="contact" className="py-24 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <p className="uppercase tracking-widest mb-4 text-sm font-semibold" style={{ color: colors.colors.accent }}>
                                {t('template.event.getInTouch')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.event.letsCreateMagic')}
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {t('template.event.scheduleComplimentaryConsultation')}
                            </p>
                            <div className="space-y-6">
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary }}
                                        >
                                            <i className="fas fa-map-marker-alt text-xl" style={{ color: colors.colors.accent }} />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.event.location')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary }}
                                        >
                                            <i className="fas fa-envelope text-xl" style={{ color: colors.colors.accent }} />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.event.email')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary }}
                                        >
                                            <i className="fas fa-phone text-xl" style={{ color: colors.colors.accent }} />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.event.phone')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="mt-8">
                                    <h4 className="font-semibold mb-4" style={{ color: colors.colors.text }}>{t('template.event.followOurWork')}</h4>
                                    <div className="flex space-x-4">
                                        {profile.social_links.slice(0, 3).map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-12 h-12 rounded-xl flex items-center justify-center transition-all"
                                                style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                                            >
                                                <i className={`fab fa-${link.platform} text-xl`} />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <form className="rounded-3xl p-8 shadow-xl" style={{ backgroundColor: colors.colors.secondary }}>
                                <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: typography.heading, color: colors.colors.text }}>
                                    {t('template.event.requestConsultation')}
                                </h3>
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.event.name')}
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                borderColor: 'white',
                                                backgroundColor: 'white',
                                            }}
                                            placeholder={t('template.event.yourName')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.event.email')}
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                borderColor: 'white',
                                                backgroundColor: 'white',
                                            }}
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.event.phone')}
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                borderColor: 'white',
                                                backgroundColor: 'white',
                                            }}
                                            placeholder="(555) 123-4567"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.event.eventType')}
                                        </label>
                                        <select
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all cursor-pointer"
                                            style={{
                                                borderColor: 'white',
                                                backgroundColor: 'white',
                                            }}
                                        >
                                            <option value="">{t('template.event.selectEventType')}</option>
                                            <option>{t('template.event.wedding')}</option>
                                            <option>{t('template.event.corporateEvent')}</option>
                                            <option>{t('template.event.socialCelebration')}</option>
                                            <option>{t('template.event.privateParty')}</option>
                                            <option>{t('template.event.other')}</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.event.tellUsAboutYourEvent')}
                                        </label>
                                        <textarea
                                            rows={4}
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all resize-none"
                                            style={{
                                                borderColor: 'white',
                                                backgroundColor: 'white',
                                            }}
                                            placeholder={t('template.event.shareYourVision')}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full py-4 rounded-xl font-medium transition-all"
                                        style={{
                                            backgroundColor: colors.colors.primary,
                                            color: colors.colors.secondary,
                                        }}
                                    >
                                        {t('template.event.requestConsultation')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 text-center" style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}>
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: typography.heading }}>
                    {profile.business_name}
                </h3>
                <p className="mb-6" style={{ color: colors.colors.secondary, opacity: 0.7 }}>
                    {t('template.event.luxuryEventPlanningAndDesign')}
                </p>
                {profile.social_links && profile.social_links.length > 0 && (
                    <div className="flex justify-center space-x-4 mb-6">
                        {profile.social_links.slice(0, 3).map((link) => (
                            <a
                                key={link.platform}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                            >
                                <i className={`fab fa-${link.platform}`} />
                            </a>
                        ))}
                    </div>
                )}
                <p className="text-sm" style={{ color: colors.colors.secondary, opacity: 0.5 }}>
                    © {new Date().getFullYear()} {profile.business_name}. {t('template.event.allRightsReserved')}
                </p>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default EventPlannerTemplate;
